import { ChangeDetectionStrategy, Component, Inject, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { ToggleComponent } from 'src/app/shared/components/toggle/toggle.component';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { environment } from 'src/environment';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { ApproverDefinitionItem, EnumApproverType } from 'src/app/models/definitionsStyle.model';
import { DistributionGroupApiService, GroupOption } from 'src/app/services/distribution-group-api.service';

export interface PopupCreateSettingsData {
  isApprovalRequired: boolean;
  isUnauthorized: boolean;
  isApiExposure: boolean;
  selectedApprovers: ApproverDefinitionItem[];
  isNotificationEnabled: boolean;
  notificationEmail: string;
}

export interface PopupCreateSettingsResult {
  isApprovalRequired: boolean;
  isUnauthorized: boolean;
  isApiExposure: boolean;
  selectedApprovers: ApproverDefinitionItem[];
  isNotificationEnabled: boolean;
  notificationEmail: string;
}

@Component({
    selector: 'que-popup-create-settings',
    imports: [CommonModule, TranslateDirective, TranslatePipe, ToggleComponent, ReactiveFormsModule, OverlayModule],
    templateUrl: './popup-create-settings.component.html',
    styleUrls: ['./popup-create-settings.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PopupCreateSettingsComponent implements OnInit {
  private dialogRef = inject(MatDialogRef<PopupCreateSettingsComponent>);
  #distributionGroupApiService = inject(DistributionGroupApiService);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PopupCreateSettingsData
  ) { }

  ngOnInit(): void {
    this.#distributionGroupApiService.getApproverGroups().subscribe(groups => {
      this.groupsList = groups.map(g => ({
        id: g.id,
        name: g.name,
        choosed: this.selectedApprovers.some(a => a.groupId === g.id)
      }));
    });
  }

  positonArea: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top'
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom'
    }
  ];

  apiExposureEnabled = environment.apiExposureEnabled ?? false;
  approvalRequiredEnabled = environment.featurs.approvalRequired == 'ON';

  isApprovalRequired = new FormControl<boolean>(this.data?.isApprovalRequired ?? false, { nonNullable: true });
  isApiExposure = new FormControl<boolean>(this.data?.isApiExposure ?? false, { nonNullable: true });
  isUnauthorized = new FormControl<boolean>(this.data?.isUnauthorized ?? false, { nonNullable: true });
  isNotificationEnabled = new FormControl<boolean>(this.data?.isNotificationEnabled ?? false, { nonNullable: true });
  notificationEmail = new FormControl<string>(this.data?.notificationEmail ?? '', { nonNullable: true, validators: [Validators.email] });

  isOpenAddRole = false;
  eAprroverManager = EnumApproverType;

  selectedApprovers: ApproverDefinitionItem[] = Array.isArray(this.data?.selectedApprovers)
    ? this.data.selectedApprovers
    : [];

  #hasRole = (role: EnumApproverType) => this.selectedApprovers.some(a => a.roleNumber === role);

  rolesList = [
    {
      name: 'firstManager',
      value: EnumApproverType.FirstManager,
      choosed: this.#hasRole(EnumApproverType.FirstManager)
    },
    {
      name: 'secondManager',
      value: EnumApproverType.SecondManager,
      choosed: this.#hasRole(EnumApproverType.SecondManager)
    }
  ];

  groupsList: { id: number, name: string, choosed: boolean }[] = [];

  changeIsApprovalRequired(state: boolean) {
    this.isApprovalRequired.setValue(state);
  }

  changeApiExposureEnabled(state: boolean) {
    this.isApiExposure.setValue(state);
  }

  changeIsUnauthorized(state: boolean) {
    this.isUnauthorized.setValue(state);
  }

  changeIsNotificationEnabled(state: boolean) {
    this.isNotificationEnabled.setValue(state);
  }

  close(): void {
    this.dialogRef.close();
  }

  confirm(): void {
    if (this.isSaveDisabled) {
      return;
    }

    this.dialogRef.close({
      isApprovalRequired: this.isApprovalRequired.value,
      isUnauthorized: this.isUnauthorized.value,
      isApiExposure: this.isApiExposure.value,
      selectedApprovers: [
        ...this.selectedRoles.map(x => ({ roleNumber: x.value }) as ApproverDefinitionItem),
        ...this.selectedGroups.map(x => ({ groupId: x.id, groupName: x.name }) as ApproverDefinitionItem)
      ],
      isNotificationEnabled: this.isNotificationEnabled.value,
      notificationEmail: this.notificationEmail.value
    });
  }

  selectApprover(item: { name: string, value: EnumApproverType; choosed: boolean }) {
    item.choosed = !item.choosed;
  }

  selectGroup(item: { id: number, name: string, choosed: boolean }) {
    item.choosed = !item.choosed;
  }

  get selectedRoles() {
    return this.rolesList.filter(x => x.choosed);
  }

  get selectedGroups() {
    return this.groupsList.filter(x => x.choosed);
  }

  get isSaveDisabled(): boolean {
    if (this.isApprovalRequired.value && this.selectedRoles.length === 0 && this.selectedGroups.length === 0) {
      return true;
    }
    if (this.isNotificationEnabled.value && (!this.notificationEmail.value || this.notificationEmail.invalid)) {
      return true;
    }
    return false;
  }

  removeApprover(item: { name: string, value: EnumApproverType; choosed: boolean }, event: MouseEvent) {
    event.stopPropagation();
    item.choosed = false;
  }

  removeGroup(item: { id: number, name: string, choosed: boolean }, event: MouseEvent) {
    event.stopPropagation();
    item.choosed = false;
  }
}